import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * - `resuming`: a device marker was found after a reload/restart; the component resumes it for the same user.
 */
type TPayByBankSetupPhase = 'idle' | 'resuming' | 'waiting' | 'failed';

interface IPayByBankSetupState {
	phase: TPayByBankSetupPhase;
	/** Persistent part: only set while waiting, so a reload can resume the countdown */
	startedAt: number | null;
	/** Owner of the marker: it is only resumed for the same user (no owner → not resumed after a reload) */
	userId: string | null;
	startWaiting(userId: string | null): void;
	resumeWaiting(): void;
	markFailed(): void;
	reset(): void;
}

export const usePayByBankSetupStore = create<IPayByBankSetupState>()(
	persist(
		(set, get) => ({
			phase: 'idle',
			startedAt: null,
			userId: null,
			startWaiting: (userId) => {
				if (get().phase === 'waiting') return;
				set({ phase: 'waiting', startedAt: Date.now(), userId });
			},
			resumeWaiting: () => {
				if (get().phase !== 'resuming') return;
				set({ phase: 'waiting' });
			},
			markFailed: () => set({ phase: 'failed' }),
			reset: () => set({ phase: 'idle', startedAt: null, userId: null }),
		}),
		{
			name: 'payByBankSetup',
			/** Persist the marker only while waiting; a persisted phase is never trusted. */
			partialize: (state) =>
				state.phase === 'waiting'
					? { startedAt: state.startedAt, userId: state.userId }
					: { startedAt: null, userId: null },
			merge: (persisted, current) => {
				const { startedAt, userId } = (persisted ?? {}) as {
					startedAt?: number | null;
					userId?: string | null;
				};
				return typeof startedAt === 'number' && typeof userId === 'string'
					? { ...current, phase: 'resuming', startedAt, userId }
					: { ...current, phase: 'idle', startedAt: null, userId: null };
			},
		}
	)
);
